/**
 * 檢核項目編輯表單 | Inspection Item Edit Form
 * 用於新增或編輯品質檢核項目
 */

import { useState, useEffect, useCallback } from 'react';
import { Modal } from '@/components/UI/Modal';
import type { InspectionItem } from '@/types/quality';
import { toast } from '@/components/UI/Toast';

interface InspectionItemFormProps {
  item?: InspectionItem; // 有值則為編輯模式
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: Omit<InspectionItem, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

// 檢核類別選項
const CATEGORY_OPTIONS = ['尺寸', '外觀', '功能', '材料', '安全', '其他'] as const;

// 檢核方法選項
const METHOD_OPTIONS = ['量測', '目視', '功能測試', '破壞性', '化學分析', '其他'] as const;

// 重要程度選項
const CRITICAL_LEVEL_OPTIONS = ['關鍵', '重要', '一般'] as const;

interface FormData {
  code: string;
  name: string;
  category: InspectionItem['category'];
  method: InspectionItem['method'];
  standard: string;
  tolerance: string;
  unit: string;
  referenceDoc: string;
  productId: string;
  processCode: string;
  isRequired: boolean;
  samplingRate: number;
  criticalLevel: InspectionItem['criticalLevel'];
  notes: string;
}

interface FormErrors {
  code?: string;
  name?: string;
  category?: string;
  method?: string;
  standard?: string;
  criticalLevel?: string;
  samplingRate?: string;
}

export function InspectionItemForm({ item, isOpen, onClose, onSave }: InspectionItemFormProps) {
  const isEditMode = !!item;

  // 表單狀態
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    code: '',
    name: '',
    category: '尺寸',
    method: '量測',
    standard: '',
    tolerance: '',
    unit: '',
    referenceDoc: '',
    productId: '',
    processCode: '',
    isRequired: true,
    samplingRate: 100,
    criticalLevel: '一般',
    notes: '',
  });

  // 驗證錯誤
  const [errors, setErrors] = useState<FormErrors>({});
  // 追蹤已被觸碰的欄位
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // 當 item 改變時，更新表單資料
  useEffect(() => {
    if (item) {
      setFormData({
        code: item.code,
        name: item.name,
        category: item.category,
        method: item.method,
        standard: item.standard,
        tolerance: item.tolerance || '',
        unit: item.unit || '',
        referenceDoc: item.referenceDoc || '',
        productId: item.productId || '',
        processCode: item.processCode || '',
        isRequired: item.isRequired,
        samplingRate: item.samplingRate || 100,
        criticalLevel: item.criticalLevel,
        notes: item.notes || '',
      });
    } else {
      // 新增模式的預設值
      setFormData({
        code: '',
        name: '',
        category: '尺寸',
        method: '量測',
        standard: '',
        tolerance: '',
        unit: '',
        referenceDoc: '',
        productId: '',
        processCode: '',
        isRequired: true,
        samplingRate: 100,
        criticalLevel: '一般',
        notes: '',
      });
    }
  }, [item, isOpen]);

  // 即時驗證單一欄位
  const validateField = useCallback((field: keyof FormData, value: any): string => {
    switch (field) {
      case 'code':
        if (!value.trim()) return '代碼不能為空';
        if (!/^[A-Z]{3}\d{3}$/.test(value)) return '代碼格式錯誤，應為 3 個字母 + 3 個數字（例：DIM001）';
        return '';
      case 'name':
        if (!value.trim()) return '名稱不能為空';
        if (value.trim().length < 2) return '名稱至少需要 2 個字元';
        return '';
      case 'category':
        if (!value) return '請選擇類別';
        return '';
      case 'method':
        if (!value) return '請選擇方法';
        return '';
      case 'standard':
        if (!value.trim()) return '標準不能為空';
        return '';
      case 'criticalLevel':
        if (!value) return '請選擇重要程度';
        return '';
      case 'samplingRate':
        if (value === '' || value === null || value === undefined) return '抽樣率不能為空';
        if (value < 0 || value > 100) return '抽樣率必須在 0-100 之間';
        return '';
      default:
        return '';
    }
  }, []);

  // 處理欄位變更
  const handleFieldChange = useCallback((field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // 如果該欄位已被觸碰，進行即時驗證
    if (touched.has(field)) {
      const error = validateField(field, value);
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[field as keyof FormErrors] = error;
        } else {
          delete newErrors[field as keyof FormErrors];
        }
        return newErrors;
      });
    } else {
      // 如果未被觸碰，只清除錯誤
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof FormErrors];
        return newErrors;
      });
    }
  }, [touched, validateField]);

  // 處理欄位失焦
  const handleFieldBlur = useCallback((field: keyof FormData) => {
    setTouched((prev) => new Set([...prev, field]));
    const error = validateField(field, formData[field]);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field as keyof FormErrors] = error;
      } else {
        delete newErrors[field as keyof FormErrors];
      }
      return newErrors;
    });
  }, [formData, validateField]);

  // 驗證表單
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    const codeError = validateField('code', formData.code);
    if (codeError) newErrors.code = codeError;

    const nameError = validateField('name', formData.name);
    if (nameError) newErrors.name = nameError;

    const categoryError = validateField('category', formData.category);
    if (categoryError) newErrors.category = categoryError;

    const methodError = validateField('method', formData.method);
    if (methodError) newErrors.method = methodError;

    const standardError = validateField('standard', formData.standard);
    if (standardError) newErrors.standard = standardError;

    const criticalLevelError = validateField('criticalLevel', formData.criticalLevel);
    if (criticalLevelError) newErrors.criticalLevel = criticalLevelError;

    const samplingRateError = validateField('samplingRate', formData.samplingRate);
    if (samplingRateError) newErrors.samplingRate = samplingRateError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateField]);

  // 提交表單
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (isSubmitting) return;

      if (!validateForm()) {
        toast.error('表單驗證失敗', '請檢查表單內容是否正確');
        return;
      }

      setIsSubmitting(true);

      try {
        onSave({
          code: formData.code,
          name: formData.name,
          category: formData.category,
          method: formData.method,
          standard: formData.standard,
          tolerance: formData.tolerance || undefined,
          unit: formData.unit || undefined,
          referenceDoc: formData.referenceDoc || undefined,
          productId: formData.productId || undefined,
          processCode: formData.processCode || undefined,
          isRequired: formData.isRequired,
          samplingRate: formData.samplingRate,
          criticalLevel: formData.criticalLevel,
          notes: formData.notes || undefined,
        });

        toast.success(
          isEditMode ? '檢核項目已更新' : '檢核項目已建立',
          `${formData.name} (${formData.code}) 已成功${isEditMode ? '更新' : '建立'}`,
          3000
        );

        onClose();
      } catch (error) {
        console.error('保存檢核項目時發生錯誤:', error);
        toast.error(
          '保存失敗',
          error instanceof Error ? error.message : '發生未預期的錯誤，請稍後再試',
          5000
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, isEditMode, isSubmitting, validateForm, onSave, onClose]
  );

  // 輸入框樣式（含錯誤狀態）
  const getInputClass = (fieldName: string) => {
    const baseClass =
      'w-full px-4 py-2.5 bg-white border-2 rounded-xl text-sm font-medium transition-all focus:ring-2 focus:ring-offset-0 focus:outline-none';
    const errorClass = errors[fieldName as keyof FormErrors]
      ? 'border-red-300 focus:ring-red-400 focus:border-red-400'
      : 'border-slate-200 focus:ring-emerald-400 focus:border-emerald-300';
    return `${baseClass} ${errorClass}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? '✏️ 編輯檢核項目' : '➕ 新增檢核項目'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* 代碼 */}
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-slate-700 mb-1">
            代碼 <span className="text-red-500">*</span>
          </label>
          <input
            id="code"
            type="text"
            value={formData.code}
            onChange={(e) => handleFieldChange('code', e.target.value.toUpperCase())}
            onBlur={() => handleFieldBlur('code')}
            className={getInputClass('code')}
            placeholder="DIM001"
            required
            aria-required="true"
            aria-invalid={errors.code && touched.has('code') ? 'true' : 'false'}
            aria-describedby={errors.code && touched.has('code') ? 'code-error' : 'code-hint'}
          />
          <p id="code-hint" className="mt-1 text-xs text-slate-500">
            格式：3 個字母 + 3 個數字（例：DIM001）
          </p>
          {errors.code && touched.has('code') && (
            <p id="code-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
              <span aria-hidden="true">⚠️</span>
              <span>{errors.code}</span>
            </p>
          )}
        </div>

        {/* 名稱 */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
            名稱 <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            onBlur={() => handleFieldBlur('name')}
            className={getInputClass('name')}
            placeholder="外徑尺寸"
            required
            aria-required="true"
            aria-invalid={errors.name && touched.has('name') ? 'true' : 'false'}
            aria-describedby={errors.name && touched.has('name') ? 'name-error' : undefined}
          />
          {errors.name && touched.has('name') && (
            <p id="name-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
              <span aria-hidden="true">⚠️</span>
              <span>{errors.name}</span>
            </p>
          )}
        </div>

        {/* 類別與方法 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-slate-700 mb-1">
              類別 <span className="text-red-500">*</span>
            </label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) =>
                handleFieldChange('category', e.target.value as InspectionItem['category'])
              }
              onBlur={() => handleFieldBlur('category')}
              className={getInputClass('category')}
              required
              aria-required="true"
              aria-invalid={errors.category && touched.has('category') ? 'true' : 'false'}
              aria-describedby={errors.category && touched.has('category') ? 'category-error' : undefined}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.category && touched.has('category') && (
              <p id="category-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                <span aria-hidden="true">⚠️</span>
                <span>{errors.category}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="method" className="block text-sm font-medium text-slate-700 mb-1">
              方法 <span className="text-red-500">*</span>
            </label>
            <select
              id="method"
              value={formData.method}
              onChange={(e) =>
                handleFieldChange('method', e.target.value as InspectionItem['method'])
              }
              onBlur={() => handleFieldBlur('method')}
              className={getInputClass('method')}
              required
              aria-required="true"
              aria-invalid={errors.method && touched.has('method') ? 'true' : 'false'}
              aria-describedby={errors.method && touched.has('method') ? 'method-error' : undefined}
            >
              {METHOD_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.method && touched.has('method') && (
              <p id="method-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                <span aria-hidden="true">⚠️</span>
                <span>{errors.method}</span>
              </p>
            )}
          </div>
        </div>

        {/* 標準 */}
        <div>
          <label htmlFor="standard" className="block text-sm font-medium text-slate-700 mb-1">
            標準 <span className="text-red-500">*</span>
          </label>
          <input
            id="standard"
            type="text"
            value={formData.standard}
            onChange={(e) => handleFieldChange('standard', e.target.value)}
            onBlur={() => handleFieldBlur('standard')}
            className={getInputClass('standard')}
            placeholder="50.00±0.05 或 無刮痕、無變形"
            required
            aria-required="true"
            aria-invalid={errors.standard && touched.has('standard') ? 'true' : 'false'}
            aria-describedby={errors.standard && touched.has('standard') ? 'standard-error' : undefined}
          />
          {errors.standard && touched.has('standard') && (
            <p id="standard-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
              <span aria-hidden="true">⚠️</span>
              <span>{errors.standard}</span>
            </p>
          )}
        </div>

        {/* 公差與單位 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="tolerance" className="block text-sm font-medium text-slate-700 mb-1">
              公差
            </label>
            <input
              id="tolerance"
              type="text"
              value={formData.tolerance}
              onChange={(e) => handleFieldChange('tolerance', e.target.value)}
              className={getInputClass('tolerance')}
              placeholder="±0.05mm"
              aria-describedby="tolerance-hint"
            />
            <p id="tolerance-hint" className="mt-1 text-xs text-slate-500">
              選填，例：±0.05mm
            </p>
          </div>

          <div>
            <label htmlFor="unit" className="block text-sm font-medium text-slate-700 mb-1">
              單位
            </label>
            <input
              id="unit"
              type="text"
              value={formData.unit}
              onChange={(e) => handleFieldChange('unit', e.target.value)}
              className={getInputClass('unit')}
              placeholder="mm"
              aria-describedby="unit-hint"
            />
            <p id="unit-hint" className="mt-1 text-xs text-slate-500">
              選填，例：mm, cm, kg, V
            </p>
          </div>
        </div>

        {/* 重要程度與抽樣率 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="criticalLevel" className="block text-sm font-medium text-slate-700 mb-1">
              重要程度 <span className="text-red-500">*</span>
            </label>
            <select
              id="criticalLevel"
              value={formData.criticalLevel}
              onChange={(e) =>
                handleFieldChange('criticalLevel', e.target.value as InspectionItem['criticalLevel'])
              }
              onBlur={() => handleFieldBlur('criticalLevel')}
              className={getInputClass('criticalLevel')}
              required
              aria-required="true"
              aria-invalid={errors.criticalLevel && touched.has('criticalLevel') ? 'true' : 'false'}
              aria-describedby={
                errors.criticalLevel && touched.has('criticalLevel') ? 'criticalLevel-error' : undefined
              }
            >
              {CRITICAL_LEVEL_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.criticalLevel && touched.has('criticalLevel') && (
              <p
                id="criticalLevel-error"
                className="mt-1.5 text-sm text-red-600 flex items-center gap-1"
                role="alert"
              >
                <span aria-hidden="true">⚠️</span>
                <span>{errors.criticalLevel}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="samplingRate" className="block text-sm font-medium text-slate-700 mb-1">
              抽樣率 (%) <span className="text-red-500">*</span>
            </label>
            <input
              id="samplingRate"
              type="number"
              min="0"
              max="100"
              step="1"
              value={formData.samplingRate}
              onChange={(e) => handleFieldChange('samplingRate', Number(e.target.value))}
              onBlur={() => handleFieldBlur('samplingRate')}
              className={getInputClass('samplingRate')}
              required
              aria-required="true"
              aria-invalid={errors.samplingRate && touched.has('samplingRate') ? 'true' : 'false'}
              aria-describedby={
                errors.samplingRate && touched.has('samplingRate') ? 'samplingRate-error' : 'samplingRate-hint'
              }
            />
            <p id="samplingRate-hint" className="mt-1 text-xs text-slate-500">
              範圍：0-100，100 代表全檢
            </p>
            {errors.samplingRate && touched.has('samplingRate') && (
              <p id="samplingRate-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                <span aria-hidden="true">⚠️</span>
                <span>{errors.samplingRate}</span>
              </p>
            )}
          </div>
        </div>

        {/* 必檢 */}
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isRequired}
              onChange={(e) => handleFieldChange('isRequired', e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm font-medium text-slate-700">必檢項目</span>
          </label>
          <p className="mt-1 text-xs text-slate-500 ml-6">勾選後此項目為必須檢驗</p>
        </div>

        {/* 參考文件 */}
        <div>
          <label htmlFor="referenceDoc" className="block text-sm font-medium text-slate-700 mb-1">
            參考文件
          </label>
          <input
            id="referenceDoc"
            type="text"
            value={formData.referenceDoc}
            onChange={(e) => handleFieldChange('referenceDoc', e.target.value)}
            className={getInputClass('referenceDoc')}
            placeholder="SPE-P-001"
            aria-describedby="referenceDoc-hint"
          />
          <p id="referenceDoc-hint" className="mt-1 text-xs text-slate-500">
            選填，相關規格文件編號
          </p>
        </div>

        {/* 關聯產品 */}
        <div>
          <label htmlFor="productId" className="block text-sm font-medium text-slate-700 mb-1">
            關聯產品
          </label>
          <input
            id="productId"
            type="text"
            value={formData.productId}
            onChange={(e) => handleFieldChange('productId', e.target.value)}
            className={getInputClass('productId')}
            placeholder="P001"
            aria-describedby="productId-hint"
          />
          <p id="productId-hint" className="mt-1 text-xs text-slate-500">
            選填，關聯的產品 ID（空白則適用於所有產品）
          </p>
        </div>

        {/* 備註 */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-1">
            備註
          </label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-emerald-400 focus:border-emerald-300 transition-all focus:outline-none resize-none"
            rows={3}
            placeholder="輸入備註事項（選填）"
            aria-describedby="notes-hint"
          />
          <p id="notes-hint" className="mt-1 text-xs text-slate-500">
            選填欄位，可記錄特殊注意事項
          </p>
        </div>

        {/* 按鈕 */}
        <div className="flex justify-end gap-3 pt-6 border-t-2 border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-xl hover:from-slate-200 hover:to-slate-300 transition-all text-sm font-semibold shadow-sm hover:shadow-md active:scale-95"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-500 text-white rounded-xl hover:from-emerald-700 hover:to-teal-600 transition-all text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                <span>處理中...</span>
              </>
            ) : (
              <>{isEditMode ? '💾 儲存變更' : '➕ 新增項目'}</>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
